import { useEffect, useId, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon } from "../components/Icons";
import ui from "../components/ui.module.css";
import { textLength } from "../data/activityAdapter";
import useSentBack from "../hooks/useSentBack";

// Remaining length is shown once the learner is this close to the limit.
const NEAR_LIMIT = 0.8;
// A draft is kept on this tablet after the learner pauses typing.
const DRAFT_MS = 600;

// Free written words (Learner Work Policy v1 §6): one short line or a
// paragraph, in the learner's own words. The text is handed in exactly as
// written, line breaks included, and is normally read by the teacher; only a
// bounded exact answer is ever compared, and only by the gateway.
//
// Unfinished text is a draft on this tablet (never a submission), so a refresh
// returns to it. Handing in is always an explicit action.
const TextEntryStep = ({ step, saved, verdict, onDone, onProgress, saving, readOnly = false }) => {
  const [text, setText] = useState(() =>
    typeof saved?.text === "string" ? saved.text : typeof saved?.draft === "string" ? saved.draft : ""
  );
  const [problem, setProblem] = useState(null);
  const [sent, setSent] = useState(readOnly);
  const draft = useRef(null);
  const fieldId = useId();
  const messageId = useId();
  const countId = useId();

  useEffect(() => () => clearTimeout(draft.current), []);

  useSentBack(verdict, () => setSent(false));

  const length = textLength(text);
  const remaining = step.maxLength - length;
  const showCount = length >= step.maxLength * NEAR_LIMIT;
  const locked = saving || sent;

  const edit = (event) => {
    let next = event.target.value;
    // The platform bound is enforced here too, by character, not UTF-16 unit.
    if (textLength(next) > step.maxLength) next = [...next].slice(0, step.maxLength).join("");
    setText(next);
    setProblem(null);
    clearTimeout(draft.current);
    draft.current = setTimeout(() => onProgress?.({ draft: next }), DRAFT_MS);
  };

  const handIn = () => {
    if (locked) return;
    if (text.trim() === "" || length < Math.max(step.minLength, 1)) {
      setProblem(
        step.minLength > 1
          ? `Write at least ${step.minLength} characters.`
          : "Write your answer first."
      );
      return;
    }
    clearTimeout(draft.current);
    setSent(true);
    onDone({ text });
  };

  const Field = step.mode === "paragraph" ? "textarea" : "input";

  return (
    <>
      {step.stimulus && (
        <img className={ui.stimulus} src={step.stimulus.src} alt={step.stimulus.alt} />
      )}
      <Prompt step={step} />
      {step.instruction && <p className={ui.subline}>{step.instruction}</p>}
      <div className={ui.textEntry}>
        <label htmlFor={fieldId} className={ui.visuallyHidden}>
          Your answer
        </label>
        <Field
          id={fieldId}
          className={step.mode === "paragraph" ? ui.textParagraph : ui.textShort}
          {...(Field === "input" ? { type: "text", enterKeyHint: "done" } : { rows: 6 })}
          value={text}
          onChange={edit}
          onKeyDown={(event) => {
            if (Field === "input" && event.key === "Enter") {
              event.preventDefault();
              handIn();
            }
          }}
          placeholder={step.placeholder || undefined}
          readOnly={locked}
          aria-invalid={Boolean(problem)}
          aria-describedby={`${messageId} ${countId}`}
          autoComplete="off"
          spellCheck
        />
        <p id={countId} className={ui.meta} aria-live="polite">
          {showCount ? `${remaining} ${remaining === 1 ? "character" : "characters"} left` : ""}
        </p>
        <p id={messageId} className={ui.mathProblem} role="alert">
          {problem || ""}
        </p>
        {!readOnly && (
          <button
            type="button"
            className={`${ui.button} ${ui.large} ${ui.primary}`}
            onClick={handIn}
            disabled={locked}
          >
            <CheckIcon size={24} />
            {saving ? "Saving…" : "Done"}
          </button>
        )}
      </div>
    </>
  );
};

export default TextEntryStep;
